import {Button} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Toggle} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Select} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split, Stack, withShade} from '@dropbox/dig-foundations';
import {DialLockMini, EndToEndWorkflowMini} from '@dropbox/dig-illustrations';
import {aiSearchLoadingGlyphVariantAtom} from 'atoms/aiSearch';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {Employee} from 'client';
import {Accordion, AccordionIconButton} from 'components/DSYS/Accordion';
import {Avatar} from 'components/DSYS/Avatar';
import {CompanyOSGlyphLoading} from 'components/DSYS/CompanyOSLogo';
import type {EditorTypographyOptions} from 'components/DSYS/editor/themes/EditorTheme';
import {EmployeeListTypeahead} from 'components/DSYS/EmployeeListTypeahead';
import {EmployeeTypeahead} from 'components/DSYS/EmployeeTypeahead';
import {EmptyState} from 'components/DSYS/EmptyState';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {Layout} from 'components/DSYS/Layout';
import {Lightbox} from 'components/DSYS/Lightbox';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StatusButton} from 'components/DSYS/StatusButton';
import {getStatusOptions, StatusButtonIcon} from 'components/DSYS/StatusButtonIcon';
import {StatusChip} from 'components/DSYS/StatusChip';
import {DemoTable} from 'components/DSYS/TableDemo';
import {Title} from 'components/DSYS/Title';
import {UpdatePost} from 'components/DSYS/UpdatePost';
import {sortEmployees} from 'helpers/utils';
import {useAtom, useAtomValue} from 'jotai';
import {useRef, useState} from 'react';

import styles from './DSYS.module.css';
import {Note, Path} from './Note';

export const StatusExample = () => {
  const statusOptions = getStatusOptions();
  const statusButtonTypes = [
    {value: 'no_status'},
    ...statusOptions.flatMap((option) => option.statusOptions),
  ];

  return (
    <Stack gap="16">
      <Title id="status">Status</Title>

      <Note
        type="DSYS/StatusButtonIcon"
        title={`<StatusButtonIcon status="on_track" />
<StatusButtonIcon status="on_track" substatus="not_started" />
<StatusButtonIcon status="on_track" size="small" />
<StatusButtonIcon status="on_track" substatus="not_started" size="small" />`}
        path="src/components/DSYS/StatusButtonIcon.tsx"
        info="StatusButtonIcon is a standalone component that can be used to display status icons."
      >
        <Stack gap="8">
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButtonIcon status={value} />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButtonIcon status={value} substatus="not_started" />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButtonIcon status={value} size="small" />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButtonIcon status={value} substatus="not_started" size="small" />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButtonIcon status={value} size="xsmall" />
              </Split.Item>
            ))}
          </Split>
        </Stack>
      </Note>

      <Note
        type="DSYS/StatusButton"
        info="Status buttons can be used to display status with optional substatus, auto update tooltip text, disabled state (which is just a disabled button), and as a label (similar to disabled, but wihout button padding.)"
        path="src/components/DSYS/StatusButton.tsx"
        title={`<StatusButton status="on_track" />
<StatusButton status="on_track" tooltipText="Auto update text" />
<StatusButton status="on_track" hideText />
<StatusButton status="on_track" substatus="no_status" />
<StatusButton status="on_track" isDisabled />
<StatusButton status="on_track" isLabel />`}
      >
        <Stack gap="8">
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButton status={value} />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButton status={value} tooltipText="Auto update text" />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButton status={value} hideText disabled />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButton status={value} substatus="no_status" />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButton status={value} disabled />
              </Split.Item>
            ))}
          </Split>
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusButton status={value} isLabel />
              </Split.Item>
            ))}
          </Split>
        </Stack>
      </Note>

      <Note
        type="DSYS/StatusChip"
        info="Status chips are a more compact way to display status."
        path="src/components/DSYS/StatusChip.tsx"
        title={`<StatusChip status="on_track" />`}
      >
        <Stack gap="8">
          <Split gap="8">
            {statusButtonTypes.map(({value}) => (
              <Split.Item key={value}>
                <StatusChip status={value} />
              </Split.Item>
            ))}
          </Split>
        </Stack>
      </Note>
    </Stack>
  );
};

export const LoadingGlyphExample = () => {
  const [variant, setVariant] = useAtom(aiSearchLoadingGlyphVariantAtom);
  const usePulse = variant === 'pulse';

  return (
    <Stack gap="16">
      <Title id="loading-glyph">Loading glyph</Title>
      <Note
        type="DSYS/CompanyOSGlyphLoading"
        path="src/components/DSYS/CompanyOSLogo.tsx"
        info="Animated CompanyOS glyph used as the chat 'thinking' indicator. Two variants: 'converge' (spins while the halves drift to center and back) and 'pulse' (halves breathe out of phase). The toggle below is persisted and also controls the live chat indicator so you can compare them in context."
        title={`<CompanyOSGlyphLoading variant="converge" />
<CompanyOSGlyphLoading variant="pulse" />`}
      >
        <Stack gap="24">
          <Box display="flex" alignItems="center" style={{gap: 8}}>
            <Toggle
              id="toggle-loading-glyph-variant"
              isToggled={usePulse}
              onChange={() => setVariant(usePulse ? 'converge' : 'pulse')}
            />
            <Text tagName="label" htmlFor="toggle-loading-glyph-variant">
              Use pulse (off = converge) — active in chat: <Text isBold>{variant}</Text>
            </Text>
          </Box>

          <Split gap="32">
            {(['converge', 'pulse'] as const).map((v) => (
              <Split.Item key={v}>
                <Stack gap="8" style={{alignItems: 'center'}}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="Medium"
                    borderStyle="Solid"
                    borderWidth="1"
                    borderColor="Border Subtle"
                    style={{width: 96, height: 96}}
                  >
                    <CompanyOSGlyphLoading variant={v} style={{fontSize: 56}} />
                  </Box>
                  <Text size="small" color="subtle">
                    {v}
                  </Text>
                </Stack>
              </Split.Item>
            ))}
          </Split>
        </Stack>
      </Note>
    </Stack>
  );
};

// DemoTable
export const DemoTableExample = () => {
  return (
    <Stack gap="16">
      <Title id="table" withAccessoryEnd={<Path path="src/dig-overrides/table.css" />}>
        Demo Table
      </Title>
      <Note
        type="DSYS/Table"
        info="Can be sorted, resized, filtered, searched, and can be selectable."
        path=" "
      >
        <Box backgroundColor="Accent Blue">
          <DemoTable />
        </Box>
      </Note>
    </Stack>
  );
};

/** RichTextArea typography demos: labels describe intent; values are the real props. */
const EDITOR_TYPOGRAPHY_PRESETS: {
  label: string;
  description: string;
  value: EditorTypographyOptions;
}[] = [
  {
    label: 'Default',
    description: 'Matches DIG Text defaults (body medium, standard color).',
    value: {},
  },
  {
    label: 'Paper document',
    description: 'Planning-style body + heading ramp and tables (profile=paper, textSize=large).',
    value: {profile: 'paper', textSize: 'large'},
  },
  {
    label: 'Large body',
    description: 'Body uses large type scale (textSize=large).',
    value: {textSize: 'large'},
  },
  {
    label: 'Subtle color',
    description: 'Same size as default, subtle text (textColor=subtle).',
    value: {textColor: 'subtle'},
  },
  {
    label: 'Bold block',
    description: 'Whole paragraph weight strong (textBold, with medium body).',
    value: {textBold: true, textSize: 'medium'},
  },
  {
    label: 'Small + inherit color',
    description: 'Smallest body scale; color follows parent (textSize=small, textColor=inherit).',
    value: {textColor: 'inherit', textSize: 'small'},
  },
  {
    label: 'Small + subtle',
    description: 'Compact secondary copy (textSize=small, textColor=subtle).',
    value: {textColor: 'subtle', textSize: 'small'},
  },
];

export const EditorExample = () => {
  const [typographyKey, setTypographyKey] = useState(0);
  const typography = EDITOR_TYPOGRAPHY_PRESETS[typographyKey]!.value;
  const editorContentRef = useRef<string>('');
  const richTextAreaRef = useRef<any>(null);

  const handleTypographyChange = (nextIndex: number) => {
    const currentContent = editorContentRef.current;
    setTypographyKey(nextIndex);
    setTimeout(() => {
      if (richTextAreaRef.current && currentContent) {
        richTextAreaRef.current.clearEditor(currentContent);
      }
    }, 0);
  };

  return (
    <Stack gap="16">
      <Title id="editor" withAccessoryEnd={<Path path="src/pages/Editor.tsx" />}>
        Editor
      </Title>
      <Note
        type="DSYS/RichTextEditor"
        path="src/components/DSYS/RichTextEditor.tsx"
        info="WYSIWYG editor on Lexical. Typography is controlled with textSize, textColor, textBold, and profile (default vs paper). Other props cover @mentions, media, tables, and more."
      >
        <Stack gap="8">
          <Text size="small" color="subtle">
            {EDITOR_TYPOGRAPHY_PRESETS[typographyKey]!.description}
          </Text>
          <Box backgroundColor="Background Base" borderRadius="Medium" paddingBottom="24">
            <RichTextArea
              ref={richTextAreaRef}
              key={typographyKey}
              editable={true}
              {...typography}
              topOffset={0}
              onChange={(state) => {
                editorContentRef.current = state.toString();
              }}
            />
          </Box>
          <Select
            id="editor-typography-select"
            defaultValue={String(typographyKey)}
            onChange={(value) => handleTypographyChange(Number(value))}
          >
            {EDITOR_TYPOGRAPHY_PRESETS.map((preset, index) => (
              <Select.Option key={`editor-typography-${index}`} value={String(index)}>
                {preset.label}
              </Select.Option>
            ))}
          </Select>
        </Stack>
      </Note>
    </Stack>
  );
};

export const UpdatesExample = () => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);

  if (!employee?.ldap) {
    return null;
  }

  return (
    <Stack gap="16">
      <Title id="update-post">UpdatePost</Title>
      <Note
        type="DSYS/UpdatePost"
        path="src/components/DSYS/UpdatePost.tsx"
        info="UpdatePost is a helper component that displays a user avatar/name/role, a timestamp, and a status update (if provided.) It's built with underlying components like `UpdatePost.Control`, `UpdatePost.User`, and `UpdatePost.Status`, which you could extend if you need to customize the layout/content"
      >
        <UpdatePost featured employee={employee} update={{created_at: new Date().toISOString()}}>
          Featured update without status
        </UpdatePost>

        {['Second update', 'First update'].map((content) => (
          <UpdatePost
            key={content}
            employee={employee}
            update={{
              comment: 'string',
              created_at: new Date().toISOString(),
              status: 'on_track',
            }}
          >
            {content}
          </UpdatePost>
        ))}

        <UpdatePost
          featured
          employee={employee}
          update={{
            comment: 'string',
            created_at: new Date().toISOString(),
            status: 'on_track',
          }}
        >
          Featured update with status
        </UpdatePost>
      </Note>
    </Stack>
  );
};

export const EmptyStateExample = () => {
  return (
    <Stack gap="16">
      <Title id="empty-state">EmptyState</Title>
      <Note
        type="DSYS/EmptyState"
        path="src/components/DSYS/EmptyState.tsx"
        info="EmptyState is a helper component that displays a title, body, image, and an optional CTA."
        title={`<EmptyState image={} title={} body={} />`}
      >
        <EmptyState
          title="These goals are confidential"
          body="Some goals contain confidential information and cannot be shared at this time"
          image={
            // This is an example of how to change a DIG illustration to a different color
            <Box className={styles.dialLock}>
              <DialLockMini width={64} altText={''} />
            </Box>
          }
        />
      </Note>

      <Note
        type="DSYS/EmptyState(CTA)"
        path="src/components/DSYS/EmptyState.tsx"
        info="EmptyState is a helper component that displays a title, body, image, and an optional CTA."
        title={`<EmptyState image={} title={} body={} cta={to, text} />`}
      >
        <EmptyState
          title="No filters applied"
          body="Apply filters to quickly find the goals that interest you most."
          ctaLink={{
            to: '/dsys',
            text: 'Apply filters',
          }}
          image={<EndToEndWorkflowMini width={64} altText={''} />}
        />
      </Note>
    </Stack>
  );
};

interface AccordionDemoItem {
  name: string;
  description?: string;
  children?: AccordionDemoItem[];
}

const demoAccordionItems: AccordionDemoItem[] = [
  {
    name: 'Section One',
    description: 'First section description',
    children: [
      {name: 'Sub-item A', description: 'Details about A'},
      {name: 'Sub-item B', description: 'Details about B'},
    ],
  },
  {name: 'Section Two', description: 'Second section description'},
];

export const AccordionExample = () => {
  const renderIconButton = (isActive: boolean, toggle: () => void) => (
    <AccordionIconButton
      isActive={isActive}
      toggle={(e) => {
        e.stopPropagation();
        toggle();
      }}
    />
  );

  const renderItem = (
    item: AccordionDemoItem,
    expanded: boolean,
    _active: boolean,
    toggle: () => void,
    level = 0
  ) => (
    <>
      <Box
        onClick={level === 0 ? toggle : undefined}
        padding="16"
        cursor="pointer"
        borderRadius="Medium"
        {...withShade({
          state: expanded ? 'animated' : undefined,
        })}
      >
        <LabelGroup
          withText={item.name}
          withSubtext={item.description}
          withLeftAccessory={item.children && renderIconButton(expanded, toggle)}
        />
      </Box>
      {expanded && item.children && (
        <Accordion items={item.children} renderItem={renderItem} level={level + 1} />
      )}
    </>
  );

  return (
    <Stack gap="16">
      <Title id="accordion">Accordion</Title>
      <Note
        type="DSYS/Accordion"
        path="src/components/DSYS/Accordion.tsx"
        info="Accordion is a helper component that displays a title, body, and an optional CTA."
        title={`<Accordion title={} body={} cta={to, text} />
<Accordion title={} body={} />`}
      >
        <Accordion items={demoAccordionItems} renderItem={renderItem} tabSize={0} />
      </Note>
    </Stack>
  );
};

export const LightboxExample = () => {
  const [open, setOpen] = useState(false);
  return (
    <Stack gap="16">
      <Title id="lightbox">Lightbox</Title>
      <Note
        type="DSYS/Lightbox"
        path="src/components/DSYS/Lightbox.tsx"
        info="Lightbox is a component that lets you slide through images / video assets."
        title={`<Lightbox title={} body={} cta={to, text} />
<Lightbox title={} body={} />`}
      >
        <Button variant="opacity" onClick={() => setOpen(true)}>
          Open Lightbox
        </Button>
        <Lightbox
          open={open}
          close={() => setOpen(false)}
          assets={[
            'https://picsum.photos/600',
            'https://picsum.photos/605',
            'https://picsum.photos/610',
            'https://picsum.photos/615',
          ]}
          index={0}
        />
      </Note>
    </Stack>
  );
};

export const TypeaheadExample = () => {
  const [selections, setSelections] = useState<Employee[]>([]);
  const [selection, setSelection] = useState<Employee | null>(null);
  const [primaryContact, setPrimaryContact] = useState<Employee | null>();

  const [isEditable, setIsEditable] = useState(true);

  return (
    <Stack gap="16">
      <Title id="typeahead">Typeahead</Title>
      <Note
        type="DSYS/EmployeeTypeahead(Multi)"
        info="EmployeeTypeahead is a component that lets you search and select a list of employees."
        title={`<Typeahead options={} onSelect={} />`}
      >
        <Box
          style={{maxWidth: 512, margin: '0 auto'}}
          borderRadius="Medium"
          padding="16"
          borderStyle="Solid"
          borderWidth="1"
          borderColor="Border Base"
        >
          <EmployeeTypeahead
            selections={selections.map(({email}) => email ?? '')}
            onMultiSelect={(employee) => setSelections([...selections, ...employee])}
            placeholder={[]}
          />
          <Stack style={{minHeight: 500}} gap="8">
            {selections.map((employee) => (
              <LabelGroup
                key={employee.user_id}
                withText={<Text isBold>{employee.name}</Text>}
                withSubtext={employee.role}
                withLeftAccessory={<Avatar user={employee} />}
              />
            ))}
          </Stack>
        </Box>
      </Note>
      <Note
        type="DSYS/EmployeeTypeahead(Single)"
        info="EmployeeTypeahead is a component that lets you search and select a single employee from a list of employees."
        title={`<Typeahead options={} onSelect={} />`}
      >
        <Box
          style={{maxWidth: 512, margin: '0 auto'}}
          borderRadius="Medium"
          padding="16"
          borderStyle="Solid"
          borderWidth="1"
          borderColor="Border Base"
        >
          <EmployeeTypeahead
            selection={selection?.email ?? ''}
            onSingleSelect={(employee) => setSelection(employee)}
            placeholder={[]}
          />
          <Stack style={{minHeight: 50}} gap="8">
            {selection && (
              <LabelGroup
                key={selection.user_id}
                withText={<Text isBold>{selection.name}</Text>}
                withSubtext={selection.role}
                withLeftAccessory={<Avatar user={selection} />}
              />
            )}
          </Stack>
        </Box>
      </Note>
      <Note
        type="DSYS/EmployeeListTypeahead"
        info="EmployeeListTypeahead builds upon EmployeeTypeahead, but also renders the selected employees as a list. `withRightAccessory` and `withOverflowMenuItems` are optional props that can be used to add additional content to each employee."
        title={`<Typeahead editable={} selections={} setSelections={} withOverflowMenuItems={} withRightAccessory={} />`}
      >
        <Box
          style={{maxWidth: 512, margin: '0 auto'}}
          borderRadius="Medium"
          padding="16"
          borderStyle="Solid"
          borderWidth="1"
          borderColor="Border Base"
        >
          <Box display="flex" alignItems="center" marginBottom="32" style={{gap: 8}}>
            <Toggle
              id="toggle-editable"
              isToggled={isEditable}
              onChange={() => setIsEditable(!isEditable)}
            />
            <Text tagName="label" htmlFor="toggle-editable">
              Editable
            </Text>
          </Box>

          <EmployeeListTypeahead
            editable={isEditable}
            selections={selections.sort(sortEmployees)}
            setSelections={setSelections}
            withOverflowMenuItems={(employee) => (
              <>
                <Menu.ActionItem
                  onClick={() =>
                    setPrimaryContact(primaryContact === employee ? undefined : employee)
                  }
                >
                  {primaryContact === employee ? 'Remove primary contact' : 'Set primary contact'}
                </Menu.ActionItem>
                <Menu.LinkItem href={`/people/${employee.ldap}`}>
                  <PersonHoverCard ldap={employee.ldap} name={employee.name} role={employee.role}>
                    <span>View profile</span>
                  </PersonHoverCard>
                </Menu.LinkItem>
              </>
            )}
            withRightAccessory={(employee) =>
              employee.user_id === primaryContact?.user_id ? (
                <Chip size="small">Primary contact</Chip>
              ) : null
            }
          />
        </Box>
      </Note>
    </Stack>
  );
};

export const OptionalTooltipExample = () => {
  const [enabled, setEnabled] = useState(false);
  return (
    <Stack gap="16">
      <Title id="optional-tooltip">OptionalTooltip</Title>
      <Note
        type="DSYS/OptionalTooltip"
        path="src/components/DSYS/OptionalTooltip.tsx"
        info="OptionalTooltip wraps around a normal tooltip, but adds an `enabled` prop for it to be conditionally shown."
        title={`<OptionalTooltip title={} body={} />`}
      >
        <OptionalTooltip enabled={enabled} title="Hovered!">
          <Button variant="opacity" onClick={() => setEnabled(!enabled)}>
            Hover me — click {enabled ? 'to hide' : 'to show'}
          </Button>
        </OptionalTooltip>
      </Note>
    </Stack>
  );
};

export const GridLayoutExample = () => {
  return (
    <Stack gap="16">
      <Title id="grid-layout">Grid Layouts</Title>
      <Note
        type="DSYS/Layout.Container"
        path="src/components/DSYS/Layout.tsx"
        info="Layout.Container is a helper component that takes up the full width of the parent container. You can use the `condensed` prop to take up less space."
        title={`<Layout.Container />
<Layout.Container size="condensed" />`}
      >
        <Stack gap="16">
          <Layout.Container>
            <Box
              borderRadius="Medium"
              backgroundColor="Highlight Red"
              width="100%"
              style={{height: 100}}
            />
          </Layout.Container>

          <Layout.Container>
            <Box
              borderRadius="Medium"
              backgroundColor="Highlight Red - Bolder"
              width="100%"
              style={{height: 100}}
            />
          </Layout.Container>
        </Stack>
      </Note>

      <Note
        type="DSYS/Layout.Nav"
        path="src/components/DSYS/Layout.tsx"
        info="Layout.Nav and Layout.Body are helper components that takes up the first 2/last 5 columns of the parent container."
        title={`<Layout.Container>
  <Layout.Nav />
  <Layout.Body />
</Layout.Container>`}
      >
        <Layout.Container>
          <Layout.Nav>
            <Box
              borderRadius="Medium"
              backgroundColor="Highlight Red"
              width="100%"
              style={{height: 100}}
            />
          </Layout.Nav>
          <Layout.Body>
            <Box
              borderRadius="Medium"
              backgroundColor="Highlight Red - Bolder"
              width="100%"
              style={{height: 100}}
            />
          </Layout.Body>
        </Layout.Container>
      </Note>
    </Stack>
  );
};
